import { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Warning } from 'shared/icons';
import api from '../../services/api';

/**
 * Alertas via Telegram — configuração autônoma (sem dev) do bot que dispara
 * alertas de infra e de campanhas. O dono cola o token do @BotFather, detecta
 * quem já mandou /start pro bot e distribui cada pessoa numa categoria.
 *
 * Wire (snake_case, contrato definido no backend):
 *   GET  /notifications/telegram/config   -> { configured, bot_token_masked, bot_username, categories: { infra: [], campaigns: [] } }
 *   PUT  /notifications/telegram/config   body { bot_token?, categories? } (bot_token omitido = mantém; null/"" = limpa)
 *   POST /notifications/telegram/discover -> { bot_username, chats: [{chat_id, username, first_name, last_text}] }
 *   POST /notifications/telegram/test     body { category } -> { sent, results: [{chat_id, ok, error_description}] }
 *
 * Após qualquer PUT recarregamos via GET em vez de confiar no corpo da
 * resposta do PUT — o contrato não define o shape de retorno do PUT.
 */
const CATEGORY_DEFS = [
    { key: 'infra', label: 'Infra / erros de sistema' },
    { key: 'campaigns', label: 'Campanhas' },
];

const Chip = ({ tone, children }) => {
    const tons = {
        ok: 'bg-success-soft text-success-text dark:bg-success-900/30 dark:text-success-300',
        warn: 'bg-warning-soft text-warning-text dark:bg-warning-900/30 dark:text-warning-300',
        off: 'bg-mono-100 text-mono-600 dark:bg-mono-800 dark:text-mono-300',
        bad: 'bg-danger-soft text-danger-text dark:bg-danger-900/30 dark:text-danger-300',
    };
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${tons[tone] || tons.off}`}>
            {children}
        </span>
    );
};

const TelegramSettings = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [tokenInput, setTokenInput] = useState('');
    const [savingToken, setSavingToken] = useState(false);
    const [tokenError, setTokenError] = useState(null);
    const [tokenNotice, setTokenNotice] = useState(null);

    const [discovering, setDiscovering] = useState(false);
    const [discoverError, setDiscoverError] = useState(null);
    const [discoveredChats, setDiscoveredChats] = useState(null);

    const [manualDrafts, setManualDrafts] = useState({
        infra: { chatId: '', label: '' },
        campaigns: { chatId: '', label: '' },
    });
    const [categoryBusy, setCategoryBusy] = useState({});
    const [categoryError, setCategoryError] = useState({});

    const [testBusy, setTestBusy] = useState({});
    const [testResults, setTestResults] = useState({});

    const loadConfig = useCallback(async () => {
        try {
            setLoadError(null);
            const res = await api.get('/notifications/telegram/config');
            setConfig(res?.data ?? res);
        } catch (err) {
            setLoadError(err.message || 'Erro ao carregar a configuração do Telegram.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const saveToken = async () => {
        const trimmed = tokenInput.trim();
        if (!trimmed) return;
        setSavingToken(true);
        setTokenError(null);
        setTokenNotice(null);
        try {
            await api.put('/notifications/telegram/config', { bot_token: trimmed });
            await loadConfig();
            setTokenInput('');
            setTokenNotice('Token salvo.');
        } catch (err) {
            setTokenError(err.message || 'Não foi possível salvar o token.');
        } finally {
            setSavingToken(false);
        }
    };

    const clearToken = async () => {
        if (
            !window.confirm(
                'Remover o token do bot?\n\nOs alertas param de ser enviados até um novo token ser configurado.',
            )
        ) {
            return;
        }
        setSavingToken(true);
        setTokenError(null);
        setTokenNotice(null);
        try {
            await api.put('/notifications/telegram/config', { bot_token: null });
            await loadConfig();
            setTokenNotice('Token removido.');
        } catch (err) {
            setTokenError(err.message || 'Não foi possível remover o token.');
        } finally {
            setSavingToken(false);
        }
    };

    const currentCategories = () => ({
        infra: (config?.categories?.infra || []).map((c) => ({ chat_id: c.chat_id, label: c.label })),
        campaigns: (config?.categories?.campaigns || []).map((c) => ({ chat_id: c.chat_id, label: c.label })),
    });

    const persistCategories = async (nextCategories, categoryKey) => {
        setCategoryBusy((prev) => ({ ...prev, [categoryKey]: true }));
        setCategoryError((prev) => ({ ...prev, [categoryKey]: null }));
        try {
            await api.put('/notifications/telegram/config', { categories: nextCategories });
            await loadConfig();
        } catch (err) {
            setCategoryError((prev) => ({
                ...prev,
                [categoryKey]: err.message || 'Não foi possível salvar os destinatários.',
            }));
        } finally {
            setCategoryBusy((prev) => ({ ...prev, [categoryKey]: false }));
        }
    };

    const addChat = async (categoryKey, chatId, label) => {
        const normalizedChatId = String(chatId || '').trim();
        if (!normalizedChatId) return;
        const base = currentCategories();
        const already = (base[categoryKey] || []).some((c) => String(c.chat_id) === normalizedChatId);
        const nextList = already
            ? base[categoryKey].map((c) =>
                  String(c.chat_id) === normalizedChatId ? { chat_id: normalizedChatId, label: label || c.label } : c,
              )
            : [...(base[categoryKey] || []), { chat_id: normalizedChatId, label: label || normalizedChatId }];
        await persistCategories({ ...base, [categoryKey]: nextList }, categoryKey);
    };

    const removeChat = async (categoryKey, chatId) => {
        const base = currentCategories();
        const nextList = (base[categoryKey] || []).filter((c) => String(c.chat_id) !== String(chatId));
        await persistCategories({ ...base, [categoryKey]: nextList }, categoryKey);
    };

    const runDiscover = async () => {
        setDiscovering(true);
        setDiscoverError(null);
        try {
            const res = await api.post('/notifications/telegram/discover');
            const data = res?.data ?? res;
            setDiscoveredChats(data?.chats || []);
        } catch (err) {
            setDiscoverError(err.message || 'Não foi possível detectar conversas. Verifique se o token foi salvo.');
        } finally {
            setDiscovering(false);
        }
    };

    const runTest = async (categoryKey) => {
        setTestBusy((prev) => ({ ...prev, [categoryKey]: true }));
        setTestResults((prev) => ({ ...prev, [categoryKey]: null }));
        try {
            const res = await api.post('/notifications/telegram/test', { category: categoryKey });
            const data = res?.data ?? res;
            setTestResults((prev) => ({ ...prev, [categoryKey]: data?.results || [] }));
        } catch (err) {
            setTestResults((prev) => ({
                ...prev,
                [categoryKey]: [{ chat_id: '-', ok: false, error_description: err.message || 'Falha ao enviar teste.' }],
            }));
        } finally {
            setTestBusy((prev) => ({ ...prev, [categoryKey]: false }));
        }
    };

    const updateManualDraft = (categoryKey, field, value) => {
        setManualDrafts((prev) => ({ ...prev, [categoryKey]: { ...prev[categoryKey], [field]: value } }));
    };

    return (
        <div className="overflow-hidden rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
            <div className="flex items-center justify-between border-b border-mono-100 px-4 py-2.5 dark:border-mono-900">
                <p className="text-sm font-semibold text-black dark:text-white">Alertas via Telegram</p>
                {!loading && !loadError ? (
                    config?.configured ? (
                        <Chip tone="ok">@{config.bot_username || 'bot conectado'}</Chip>
                    ) : (
                        <Chip tone="off">não configurado</Chip>
                    )
                ) : null}
            </div>

            {loading ? (
                <p className="px-4 py-6 text-center text-xs text-mono-400">Carregando…</p>
            ) : loadError ? (
                <div className="px-4 py-6 text-center">
                    <p className="text-xs text-danger-600 dark:text-danger-400">{loadError}</p>
                    <button
                        type="button"
                        onClick={loadConfig}
                        className="mt-2 rounded-md border border-mono-300 px-2 py-0.5 text-[11px] font-semibold text-mono-600 dark:border-mono-700 dark:text-mono-300"
                    >
                        Tentar novamente
                    </button>
                </div>
            ) : (
                <div className="space-y-4 px-4 py-4">
                    {/* Token do bot */}
                    <div>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-mono-500">
                            Token do bot
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            <input
                                type="password"
                                value={tokenInput}
                                onChange={(e) => setTokenInput(e.target.value)}
                                placeholder={config?.bot_token_masked || '123456:ABC-...'}
                                autoComplete="off"
                                className="min-w-[240px] flex-1 rounded-md border border-mono-300 bg-white px-2 py-1.5 font-mono text-xs text-mono-900 outline-none focus:ring-2 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                            />
                            <button
                                type="button"
                                onClick={saveToken}
                                disabled={!tokenInput.trim() || savingToken}
                                className="rounded-md bg-info-500 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {savingToken ? 'Salvando…' : 'Salvar'}
                            </button>
                            {config?.configured ? (
                                <button
                                    type="button"
                                    onClick={clearToken}
                                    disabled={savingToken}
                                    className="rounded-md border border-mono-300 px-2.5 py-1.5 text-xs font-semibold text-mono-600 transition hover:border-danger-500 hover:text-danger-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-300"
                                >
                                    Remover token
                                </button>
                            ) : null}
                        </div>
                        <p className="mt-1 text-[11px] text-mono-400">
                            {config?.configured
                                ? `Token atual: ${config.bot_token_masked || 'configurado'}. O campo acima só é enviado ao Salvar se estiver preenchido.`
                                : 'Nenhum token configurado ainda.'}
                        </p>
                        {tokenError ? (
                            <p className="mt-1 text-[11px] text-danger-600 dark:text-danger-400">{tokenError}</p>
                        ) : null}
                        {tokenNotice ? (
                            <p className="mt-1 text-[11px] text-success-text dark:text-success-300">{tokenNotice}</p>
                        ) : null}
                    </div>

                    {/* Detectar conversas */}
                    <div className="rounded-lg border border-mono-100 p-3 dark:border-mono-900">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-mono-500">
                                Detectar conversas
                            </p>
                            <button
                                type="button"
                                onClick={runDiscover}
                                disabled={discovering || !config?.configured}
                                title={!config?.configured ? 'Salve o token do bot primeiro' : undefined}
                                className="rounded-md border border-info-500 px-2.5 py-1 text-[11px] font-semibold text-info-600 transition hover:bg-info-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 dark:text-info-300"
                            >
                                {discovering ? 'Detectando…' : 'Detectar conversas'}
                            </button>
                        </div>
                        {discoverError ? (
                            <p className="mt-2 text-[11px] text-danger-600 dark:text-danger-400">{discoverError}</p>
                        ) : null}
                        {discoveredChats ? (
                            discoveredChats.length === 0 ? (
                                <p className="mt-2 text-[11px] text-mono-400">
                                    Nenhuma conversa encontrada. Peça para a pessoa abrir o bot no Telegram e enviar
                                    /start.
                                </p>
                            ) : (
                                <div className="mt-2 divide-y divide-mono-50 dark:divide-mono-900/60">
                                    {discoveredChats.map((chat) => (
                                        <div
                                            key={chat.chat_id}
                                            className="flex flex-wrap items-center justify-between gap-2 py-1.5"
                                        >
                                            <div>
                                                <p className="text-xs font-medium text-black dark:text-white">
                                                    {chat.username ? `@${chat.username}` : chat.first_name || 'sem nome'}
                                                </p>
                                                <p className="text-[10px] text-mono-400">
                                                    chat_id {chat.chat_id}
                                                    {chat.last_text ? ` · "${String(chat.last_text).slice(0, 40)}"` : ''}
                                                </p>
                                            </div>
                                            <div className="flex gap-1.5">
                                                {CATEGORY_DEFS.map((cat) => (
                                                    <button
                                                        key={cat.key}
                                                        type="button"
                                                        onClick={() =>
                                                            addChat(
                                                                cat.key,
                                                                chat.chat_id,
                                                                chat.username
                                                                    ? `@${chat.username}`
                                                                    : chat.first_name || String(chat.chat_id),
                                                            )
                                                        }
                                                        disabled={categoryBusy[cat.key]}
                                                        className="rounded-md border border-mono-300 px-2 py-0.5 text-[10px] font-semibold text-mono-600 transition hover:border-info-500 hover:text-info-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-300"
                                                    >
                                                        + {cat.key === 'infra' ? 'infra' : 'campanhas'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        ) : null}
                    </div>

                    {/* Destinatários por categoria */}
                    <div className="space-y-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-mono-500">
                            Destinatários por categoria
                        </p>
                        {CATEGORY_DEFS.map((cat) => {
                            const chats = config?.categories?.[cat.key] || [];
                            const draft = manualDrafts[cat.key];
                            const results = testResults[cat.key];
                            return (
                                <div key={cat.key} className="rounded-lg border border-mono-100 p-3 dark:border-mono-900">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="text-sm font-medium text-black dark:text-white">{cat.label}</p>
                                        <button
                                            type="button"
                                            onClick={() => runTest(cat.key)}
                                            disabled={testBusy[cat.key] || chats.length === 0}
                                            title={chats.length === 0 ? 'Adicione ao menos um destinatário' : undefined}
                                            className="rounded-md border border-mono-300 px-2.5 py-1 text-[11px] font-semibold text-mono-600 transition hover:border-info-500 hover:text-info-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-300"
                                        >
                                            {testBusy[cat.key] ? 'Enviando…' : 'Enviar teste'}
                                        </button>
                                    </div>

                                    {chats.length === 0 ? (
                                        <p className="mt-2 text-[11px] text-mono-400">
                                            Nenhum destinatário nesta categoria.
                                        </p>
                                    ) : (
                                        <div className="mt-2 divide-y divide-mono-50 dark:divide-mono-900/60">
                                            {chats.map((chat) => (
                                                <div
                                                    key={chat.chat_id}
                                                    className="flex items-center justify-between gap-2 py-1"
                                                >
                                                    <p className="text-xs text-black dark:text-white">
                                                        {chat.label}{' '}
                                                        <span className="text-mono-400">· {chat.chat_id}</span>
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeChat(cat.key, chat.chat_id)}
                                                        disabled={categoryBusy[cat.key]}
                                                        className="rounded-md border border-mono-300 px-2 py-0.5 text-[10px] font-semibold text-mono-500 transition hover:border-danger-500 hover:text-danger-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-400"
                                                    >
                                                        remover
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                        <input
                                            value={draft.chatId}
                                            onChange={(e) => updateManualDraft(cat.key, 'chatId', e.target.value)}
                                            placeholder="chat_id"
                                            className="w-[120px] rounded-md border border-mono-300 px-2 py-1 text-[11px] text-mono-900 outline-none focus:ring-1 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                                        />
                                        <input
                                            value={draft.label}
                                            onChange={(e) => updateManualDraft(cat.key, 'label', e.target.value)}
                                            placeholder="nome (label)"
                                            className="w-[140px] rounded-md border border-mono-300 px-2 py-1 text-[11px] text-mono-900 outline-none focus:ring-1 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                                        />
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                await addChat(cat.key, draft.chatId, draft.label);
                                                setManualDrafts((prev) => ({
                                                    ...prev,
                                                    [cat.key]: { chatId: '', label: '' },
                                                }));
                                            }}
                                            disabled={!draft.chatId.trim() || categoryBusy[cat.key]}
                                            className="rounded-md border border-mono-300 px-2 py-1 text-[11px] font-semibold text-mono-600 transition hover:border-info-500 hover:text-info-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-300"
                                        >
                                            Adicionar
                                        </button>
                                    </div>

                                    {categoryError[cat.key] ? (
                                        <p className="mt-1 text-[11px] text-danger-600 dark:text-danger-400">
                                            {categoryError[cat.key]}
                                        </p>
                                    ) : null}

                                    {results ? (
                                        <div className="mt-2 space-y-1">
                                            {results.map((r) => (
                                                <p
                                                    key={r.chat_id}
                                                    className="flex items-center gap-1.5 text-[11px] text-mono-500"
                                                >
                                                    {r.ok ? (
                                                        <CheckCircle size={13} className="shrink-0 text-green-500" />
                                                    ) : (
                                                        <Warning size={13} className="shrink-0 text-red-500" />
                                                    )}
                                                    <span>{r.chat_id}</span>
                                                    {!r.ok && r.error_description ? (
                                                        <span className="text-danger-600 dark:text-danger-400">
                                                            — {r.error_description}
                                                        </span>
                                                    ) : null}
                                                </p>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>

                    {/* Tutorial embutido — configuração autônoma, sem dev */}
                    <details className="rounded-lg border border-mono-100 p-3 dark:border-mono-900">
                        <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-mono-500">
                            Como configurar (passo a passo)
                        </summary>
                        <ol className="mt-2 list-decimal space-y-1.5 pl-4 text-[11px] text-mono-500">
                            <li>
                                No Telegram, fale com <strong>@BotFather</strong> → envie <code>/newbot</code> → copie o
                                token gerado.
                            </li>
                            <li>
                                Cole o token no campo acima e clique em <strong>Salvar</strong>.
                            </li>
                            <li>
                                Cada pessoa que vai receber alertas (ex.: @userA para infra, @userB para campanhas)
                                precisa abrir o bot criado no Telegram e enviar <code>/start</code>.
                            </li>
                            <li>
                                Clique em <strong>Detectar conversas</strong> e adicione cada pessoa à categoria certa.
                            </li>
                            <li>
                                Use <strong>Enviar teste</strong> para validar o recebimento.
                            </li>
                        </ol>
                        <p className="mt-2 text-[11px] text-mono-400">
                            Nota: o bot não consegue iniciar uma conversa — a pessoa <strong>precisa</strong> mandar{' '}
                            <code>/start</code> para ele antes de aparecer em "Detectar conversas".
                        </p>
                    </details>
                </div>
            )}
        </div>
    );
};

export default TelegramSettings;
